import { useForm as useInertiaForm } from "@inertiajs/vue3";
import { ref, reactive, computed, watch, isRef, nextTick } from "vue";
import type { MaybeRef } from "vue";
import { toReactive, watchDeep } from "@vueuse/core";
import { useFetch } from "@/helpers/composables/useFetch";
import NProgress from "nprogress";

export function getFirstInvalidInputElement({
    className
} = {
    className: "input-folder"
}) {
    let detailsElement;
    let inputElement;
    let detailsInputElement;

    const errorMsgElement = document.querySelector('.invalid-state.input-support-text:not(.certify-warning):not(:empty):not([hidden])');
    
    if(errorMsgElement){
        const grandparentIsInputFolder = errorMsgElement?.parentElement?.parentElement?.classList.contains(className);
        const parentIsInputFolder = errorMsgElement?.parentElement?.classList.contains(className);
        
        if(grandparentIsInputFolder){
            detailsElement = errorMsgElement?.parentElement?.parentElement?.querySelector("details");
            inputElement = errorMsgElement?.parentElement?.parentElement?.querySelector("input");
        }else if(parentIsInputFolder){
            detailsElement = errorMsgElement?.parentElement?.querySelector("details");
            inputElement = errorMsgElement?.parentElement?.querySelector("input");
        }
    }
    return errorMsgElement ? {
        ...(inputElement ?? errorMsgElement ?? {}),
        scrollToAndFocus: ()=>{
            if(detailsElement){
                detailsElement.open = true;
                detailsInputElement = detailsElement.querySelector("input");
                if(!detailsInputElement){
                    detailsElement.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"})
                }else{
                    detailsInputElement.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"})
                    nextTick(()=>{
                        detailsInputElement.focus();
                    });
                }
            }
            else if(inputElement){
                inputElement?.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"})
                nextTick(()=>{
                    inputElement?.focus?.();
                });
            }else{
                errorMsgElement?.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"})
            }
        }
    } : null;
}

export function useForm({
    useInertia,
    inertiaAlias,
    schema,
    abortEarly = false,
    initialErrors = {},
    // Error keys that are not Yup schema paths (e.g. server bag keys).
    // Preserved during revalidation so validateSyncAt is not run on them.
    preserveErrorKeys = [] as string[],
}: {
    useInertia: MaybeRef<boolean>;
    inertiaAlias?: string;
    schema: any;
    abortEarly?: boolean;
    initialErrors?: any;
    preserveErrorKeys?: string[];
}) {
    let form = {
        errors: {},
    };
    const defaultValues = schema.getDefault();
    let clientSideValid = ref(false);
    let errorsToAdd = ref({ ...initialErrors });
    function joinErrors(errors){
        return Object.keys(errors).reduce((acc, key) => {
            if(key?.includes(".")){
                key = key.split(".").at(-1);
            }
            acc[key] = Array.isArray(errors[key]) ? errors[key].join(', ') : errors[key];
            return acc;
        }, {});
    }
    if (useInertia) {
        form = inertiaAlias
            ? useInertiaForm(inertiaAlias, { ...defaultValues })
            : useInertiaForm({ ...defaultValues });
    } else {
        form = reactive({
            ...defaultValues,
            errors: {},
            processing: false,
            put(url, payload, options = {}) {
                if (clientSideValid.value !== false) {
                    let { data } = useFetch(url, {
                        ...options,
                        async beforeFetch(ctx) {
                            NProgress.start();
                            form.processing = true;
                            if (
                                options.beforeFetch &&
                                typeof options.beforeFetch === "function"
                            ) {
                                await options.beforeFetch(ctx);
                            }

                            ctx.options.headers = {
                                ...ctx.options.headers,
                                Accept: 'application/json',
                            }

                            return ctx;
                        },
                        async afterFetch(ctx) {
                            if (
                                options.afterFetch &&
                                typeof options.afterFetch === "function"
                            ) {
                                ctx = await options.afterFetch(ctx);
                            }
                            if (NProgress.isStarted()) {
                                NProgress.done();
                            }
                            form.processing = false;
                            return ctx;
                        },
                        onFetchError(ctx) {
                            form.errors = joinErrors(ctx.data.errors ?? ctx.data.error ?? {});
                            if (NProgress.isStarted()) {
                                NProgress.done();
                            }
                            form.processing = false;
                            return ctx;
                        },
                    })
                        .put(payload)
                        .json();
                    return data;
                }
            },
            post: (url, payload, options = {}) => {
                if (clientSideValid.value !== false) {
                    let { data } = useFetch(url, {
                        ...options,
                        async beforeFetch(ctx) {
                            NProgress.start();
                            form.processing = true;
                            if (
                                options.beforeFetch &&
                                typeof options.beforeFetch === "function"
                            ) {
                                await options.beforeFetch(ctx);
                            }

                            ctx.options.headers = {
                                ...ctx.options.headers,
                                Accept: 'application/json',
                            }

                            return ctx;
                        },
                        async afterFetch(ctx) {
                            if (
                                options.afterFetch &&
                                typeof options.afterFetch === "function"
                            ) {
                                ctx = await options.afterFetch(ctx);
                            }
                            if (NProgress.isStarted()) {
                                NProgress.done();
                            }
                            form.processing = false;
                            return ctx;
                        },
                        onFetchError(ctx) {
                            if(ctx?.data?.errors){
                                form.errors = joinErrors(ctx.data.errors ?? ctx.data.error ?? {});
                            }
                            if (NProgress.isStarted()) {
                                NProgress.done();
                            }
                            form.processing = false;
                            return ctx;
                        },
                    })
                        .post(payload)
                        .json();
                    return data;
                }
            },
            transform: null,
            reset: () => {
                Object.keys(defaultValues).forEach((key) => {
                    form[key] = defaultValues[key];
                });
            },
            defaults: (newDefaults) => {
                Object.keys(defaultValues).forEach((key) => {
                    defaultValues[key] = newDefaults[key];
                });
            }
        });
        form.transform = (fn) => {
            const result = fn(form);
            return result;
        };
    }

    const dirty = computed(() => {
        return Object.keys(form).some(
            (key) => form[key] !== defaultValues[key]
        );
    });

    const computedForm = computed({
        get() {
            if (useInertia) {
                return form;
            }
            return form.transform((form) => ({
                ...form,
                errors: form.errors,
                dirty: dirty.value
            }));
        },
        set(newForm) {
            form = newForm;
        }
    });
    const validate = async (options: { all?: boolean; preserveErrorKeys?: string[] } = { all: false }) => {
        const { all } = options;
        const keysToPreserve = new Set(options.preserveErrorKeys ?? preserveErrorKeys);
        let abortEarlyValue = isRef(abortEarly) ? abortEarly.value : abortEarly;
        try {
            let response = await schema.validate(form, { abortEarly: abortEarlyValue });

            clientSideValid.value = true;
            if (useInertia) {
                form.clearErrors();

                if(Object.keys(errorsToAdd.value).length > 0){
                    for(let err in errorsToAdd.value){
                        form.setError(err, errorsToAdd.value[err]);
                    }
                    errorsToAdd.value = {};
                }
            } else {
                form.errors = errorsToAdd.value;
                errorsToAdd.value = {};
            }
        } catch (e) {
            if (e.name !== "ValidationError") {
                throw e;
            }
            if (useInertia) {
                if(abortEarlyValue){
                    for(let err in form.errors){
                        if (keysToPreserve.has(err)) {
                            continue;
                        }
                        form.clearErrors(err);
                        let errorField = err.split('-')[0];
                        if(form[errorField]){
                            err = errorField;
                        }
                        try{

                            let value = schema.validateSyncAt(err, form);
                        }catch(e){
                            form.setError(err, e.message);
                        }
                    }
                    form.setError(e.path, e.message);   
                }else{
                    for (let err in form.errors) {
                        if (keysToPreserve.has(err)) {
                            continue;
                        }
                        if (!e.inner[err]) {
                            form.clearErrors(err);
                            let errorField = err.split('-')[0];
                            if(form[errorField]){
                                err = errorField;
                            }
                            try{
                                schema.validateSyncAt(err, form);
                            }catch(e){
                                form.setError(err, e.message);
                            }
                        }
                    }

                    form.setError(
                        e.inner.reduce((acc, err) => {
                            //if acc[err.path] exists, then set it to err.path + index
                            if (acc[err.path]) {
                                const errorIndex = e.inner.filter(item => item.path == err.path).findIndex(i => i.message === err.message);
                                acc[err.path + '-'+ (+errorIndex+1)] = err.message;
                            } else {
                                acc[err.path] = err.message;
                            }
                            return acc;
                        }, {})
                    );
                }
            } else {
                if(abortEarlyValue){
                    form.errors = {
                        ...form.errors,
                        [e.path]: e.message
                    };
                }else{
                    form.errors = e.inner.reduce((acc, err) => {
                        const isWatchedField = fieldsToWatch.includes(err.path);
                        const hasChanged = fieldValuesChanged[err.path] || all === true;
                        
                        if ((isWatchedField && hasChanged) || !isWatchedField) {
                            if (acc[err.path]) {
                                const errorIndex = e.inner.filter(item => item.path == err.path).findIndex(i => i.message === err.message);
                                acc[err.path + '-'+ (+errorIndex+1)] = err.message;
                            } else {
                                acc[err.path] = err.message;
                            }
                        }

                        if(all === true) fieldValuesChanged[err.path] = true;
                        return acc;
                    }, {});

                }
                
            }

            clientSideValid.value = false;
        }
    };
    form.validate = validate;

    const fieldsToWatch = Object.keys(schema.fields);
    const fieldValuesChanged = reactive(
        fieldsToWatch.reduce((acc, field) => {
            acc[field] = false;
            return acc;
        }, {})
    );
    for (const field of fieldsToWatch) {
        watchDeep(
            () => form[field],
            async (newVal, oldVal) => {
                if(Array.isArray(newVal) && Array.isArray(oldVal)){
                    await validate();
                }
                if (newVal !== oldVal || newVal === "") {
                    fieldValuesChanged[field] = true;
                    await validate();
                }
            }
        );
    }

    return form;
}
